import { z } from 'zod';
import { PlayerIdSchema } from '../ids';
import { TimestampMsSchema } from '../time';
import { ItemAmountsSchema, ItemIdSchema, QuantitySchema } from './items';

/** Scene id of the camp: each exterior scene has at most one camp (PrimeraVersion.md §11). */
export const CampIdSchema = z.string().min(1).max(64);
export type CampId = z.infer<typeof CampIdSchema>;

/**
 * Public state of a camp, as every player near it (and its owner, wherever they are) sees it.
 * The stock is not here: it grows lazily and is read with the camp view.
 */
export const CampStateSchema = z.object({
  id: CampIdSchema,
  owner: z.object({ playerId: PlayerIdSchema, displayName: z.string() }).nullable(),
  /** Looted and not repaired yet: production is suspended. */
  damaged: z.boolean(),
  /** Game time until which it cannot be looted again; null when unprotected. */
  protectedUntil: TimestampMsSchema.nullable(),
  /** The last loot, for the owner to see what was lost. */
  lastLoot: z
    .object({ by: z.string(), quantity: QuantitySchema, at: TimestampMsSchema })
    .nullable(),
});
export type CampState = z.infer<typeof CampStateSchema>;

/**
 * The camp settled at the server time of the response: its state, stock and rules. Clients may
 * project the stock forward with the shared `settleProduction` while it is owned and undamaged.
 */
export const CampViewSchema = CampStateSchema.extend({
  item: ItemIdSchema,
  stock: QuantitySchema,
  /** Game time up to which production is accounted for. */
  settledAt: z.number().nonnegative(),
  ratePerHour: z.number().positive(),
  capacity: z.number().int().positive(),
  claimCost: ItemAmountsSchema,
  repairCost: ItemAmountsSchema,
});
export type CampView = z.infer<typeof CampViewSchema>;
